import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Loader2, AlertCircle } from "lucide-react";
import { useClientAuth } from "../hooks/useClientAuth";

export default function MobileLoginPage() {
  const { login, error } = useClientAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [identifier, setIdentifier] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await login(identifier.trim().toLowerCase(), pin);
    setBusy(false);
    if (ok) nav(decodeURIComponent(params.get("next") || "/m/home"), { replace: true });
  };

  return (
    <section
      className="min-h-screen bg-[var(--lc-cream)] grid place-items-center px-5"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-white border border-[var(--lc-line)] rounded-2xl p-7"
        data-testid="mobile-login-form"
      >
        <Link to="/" className="inline-flex items-center gap-2 leading-none mb-6">
          <span className="font-serif-display text-xl tracking-tight">LONGEV</span>
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--lc-moss)]" />
          <span className="font-serif-display italic text-base">circle</span>
        </Link>
        <div className="eyebrow text-[var(--lc-moss)] mb-2">Member sign-in</div>
        <h1 className="font-serif-display text-3xl leading-tight">
          Welcome <span className="italic">back.</span>
        </h1>
        <p className="text-sm text-[var(--lc-muted)] mt-2">
          Enter your email and the PIN your coach gave you.
        </p>

        <div className="mt-6 space-y-3">
          <input
            autoFocus
            type="email"
            autoComplete="email"
            inputMode="email"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            placeholder="you@email.com"
            className="w-full px-4 py-3.5 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-md text-[15px] focus:outline-none focus:border-[var(--lc-ink)]"
            data-testid="mobile-login-email"
          />
          <input
            type="password"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            pattern="[0-9]{4,6}"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            placeholder="PIN"
            className="w-full px-4 py-3.5 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-md text-[15px] tracking-[0.3em] focus:outline-none focus:border-[var(--lc-ink)]"
            data-testid="mobile-login-pin"
          />
        </div>

        {error && (
          <div className="mt-4 flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md" data-testid="mobile-login-error">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={busy || !identifier || pin.length < 4}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 bg-[var(--lc-ink)] text-[var(--lc-cream)] text-[12px] font-bold tracking-[0.18em] uppercase rounded-md hover:bg-[var(--lc-moss-dark)] transition-colors disabled:opacity-50"
          data-testid="mobile-login-submit"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
          Sign in
        </button>

        <p className="mt-5 text-[11px] text-center text-[var(--lc-muted)]">
          Haven't got a PIN yet?{" "}
          <a href="https://wa.me/41795551234" className="underline">Contact your coach</a>.
        </p>
      </form>
    </section>
  );
}
